import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const POPOVER_WIDTH = 260;

const BAD_REASONS = [
  "wrong seniority", "wrong function", "not actually departed",
  "score contradicts data", "too stale", "wrong company",
];
const GOOD_REASONS = [
  "exactly the profile", "great seniority",
  "ideal recency", "score too low for this",
];

export default function FeedbackControl({ lead, token, onSaved }) {
  const [open, setOpen] = useState(false);
  const [pendingVerdict, setPendingVerdict] = useState(null);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [popoverStyle, setPopoverStyle] = useState({});
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const computePosition = () => {
    if (!triggerRef.current) return {};
    const rect = triggerRef.current.getBoundingClientRect();
    const left = Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - 12);
    const maxH = 320;
    const openUpward = rect.bottom + maxH > window.innerHeight - 12;
    if (openUpward) {
      return {
        position: 'fixed',
        bottom: window.innerHeight - rect.top + 6,
        left,
        width: POPOVER_WIDTH,
        maxHeight: maxH,
        overflowY: 'auto',
        zIndex: 1000,
      };
    }
    return {
      position: 'fixed',
      top: rect.bottom + 6,
      left,
      width: POPOVER_WIDTH,
      maxHeight: maxH,
      overflowY: 'auto',
      zIndex: 1000,
    };
  };

  const openPopover = (verdict, e) => {
    e.stopPropagation();
    if (open && pendingVerdict === verdict) { setOpen(false); return; }
    setPendingVerdict(verdict);
    setSelectedReasons([]);
    setNote('');
    setError(null);
    setPopoverStyle(computePosition());
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e) => {
      const inPopover = popoverRef.current && popoverRef.current.contains(e.target);
      const inTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      if (!inPopover && !inTrigger) setOpen(false);
    };
    const onClose = () => setOpen(false);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [open]);

  const toggleReason = (r) =>
    setSelectedReasons(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);

  const handleSave = async (e) => {
    e.stopPropagation();
    setSaving(true);
    setError(null);
    try {
      const body = {
        verdict: pendingVerdict,
        ...(selectedReasons.length ? { reasons: selectedReasons } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      };
      const res = await fetch(`${API}/staged-leads/${lead.id}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.status !== 201) {
        const d = await res.json();
        throw new Error(d.error || `HTTP ${res.status}`);
      }
      setOpen(false);
      onSaved(pendingVerdict);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const verdict = lead.feedback_verdict;
  const reasons = pendingVerdict === 'bad' ? BAD_REASONS : GOOD_REASONS;

  const popover = (
    <div
      ref={popoverRef}
      onClick={e => e.stopPropagation()}
      style={{
        ...popoverStyle,
        background: 'var(--glass-card)',
        backdropFilter: 'blur(12px)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        boxShadow: 'var(--card-shadow)',
        padding: '12px 14px',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text-1)', marginBottom: 8 }}>
        {pendingVerdict === 'good' ? 'Good lead' : 'Bad lead'}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
        {reasons.map(r => {
          const sel = selectedReasons.includes(r);
          return (
            <button
              key={r}
              onClick={() => toggleReason(r)}
              style={{
                fontSize: 10, padding: '2px 7px', borderRadius: 4, cursor: 'pointer',
                fontWeight: sel ? 600 : 400,
                background: sel
                  ? (pendingVerdict === 'good' ? 'rgba(45,212,191,.12)' : 'rgba(224,106,90,.12)')
                  : 'rgba(127,168,188,.08)',
                border: sel
                  ? (pendingVerdict === 'good' ? '1px solid rgba(45,212,191,.4)' : '1px solid rgba(224,106,90,.4)')
                  : '1px solid rgba(127,168,188,.15)',
                color: sel
                  ? (pendingVerdict === 'good' ? 'var(--accent)' : 'var(--negative)')
                  : 'var(--text-3)',
              }}
            >{r}</button>
          );
        })}
      </div>
      <textarea
        value={note}
        onChange={e => setNote(e.target.value)}
        maxLength={500}
        placeholder="Optional note"
        rows={2}
        style={{
          width: '100%', boxSizing: 'border-box', fontSize: 12, padding: '6px 8px',
          background: 'var(--input-bg)', border: '1px solid var(--line)',
          color: 'var(--text-1)', borderRadius: 8, marginBottom: 8, outline: 'none',
          resize: 'vertical',
        }}
      />
      {error && <div style={{ color: 'var(--negative)', fontSize: 11, marginBottom: 6 }}>{error}</div>}
      <button
        onClick={handleSave}
        disabled={saving}
        className="r-btn r-btn-primary"
        style={{ height: 26, fontSize: 11 }}
      >{saving ? 'Saving…' : 'Save'}</button>
    </div>
  );

  return (
    <span
      ref={triggerRef}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      onClick={e => e.stopPropagation()}
    >
      <button
        onClick={e => openPopover('good', e)}
        style={{
          width: 24, height: 24, borderRadius: 5,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', fontSize: 10,
          background: verdict === 'good' ? 'rgba(45,212,191,.12)' : 'transparent',
          border: verdict === 'good' ? '1px solid rgba(45,212,191,.5)' : '1px solid var(--line)',
          color: verdict === 'good' ? 'var(--accent)' : 'var(--text-4)',
        }}
        onMouseEnter={e => {
          if (verdict !== 'good') {
            e.currentTarget.style.background = 'rgba(45,212,191,.06)';
            e.currentTarget.style.borderColor = 'rgba(45,212,191,.25)';
            e.currentTarget.style.color = 'var(--accent)';
          }
        }}
        onMouseLeave={e => {
          if (verdict !== 'good') {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'var(--line)';
            e.currentTarget.style.color = 'var(--text-4)';
          }
        }}
        title="Good lead"
      >▲</button>
      <button
        onClick={e => openPopover('bad', e)}
        style={{
          width: 24, height: 24, borderRadius: 5,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', fontSize: 10,
          background: verdict === 'bad' ? 'rgba(224,106,90,.12)' : 'transparent',
          border: verdict === 'bad' ? '1px solid rgba(224,106,90,.5)' : '1px solid var(--line)',
          color: verdict === 'bad' ? 'var(--negative)' : 'var(--text-4)',
        }}
        onMouseEnter={e => {
          if (verdict !== 'bad') {
            e.currentTarget.style.background = 'rgba(224,106,90,.06)';
            e.currentTarget.style.borderColor = 'rgba(224,106,90,.25)';
            e.currentTarget.style.color = 'var(--negative)';
          }
        }}
        onMouseLeave={e => {
          if (verdict !== 'bad') {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'var(--line)';
            e.currentTarget.style.color = 'var(--text-4)';
          }
        }}
        title="Bad lead"
      >▼</button>
      {open && createPortal(popover, document.body)}
    </span>
  );
}
